import React from "react";
import {
  Calendar,
  Callout,
  Checkbox,
  DayOfWeek,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  Dropdown,
  FocusTrapZone,
  IBasePickerSuggestionsProps,
  IDropdownOption,
  IPersonaProps,
  MessageBar,
  MessageBarType,
  NormalPeoplePicker,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import { useParams } from "react-router-dom";
import { ToastContext } from "../../common/ToastProvider";
import {
  getAllCustomers,
  uptimeFileUpload,
} from "../../../services/customers/CustomerService";
import { exportExcelSheet, getFileTypeErrorMessage, parseJwt, validateFileType } from "../../../services/UtilService";
import { childrenGap, DayPickerStrings, FILE_UPLOAD_CONFIG, FILE_UPLOAD_MODULES } from "../../common/Constants";
import moment from "moment";
import { ICustomers } from "../../../vm";

export interface ManageImportDialogProps {
  handleImportDialogClose: Function;
  dataCenterOptions: IDropdownOption[];
  datacenterId: any;
  date: Date;
  customer: ICustomers;
}

let calendarButtonElement: HTMLElement;

// const DayPickerStrings = {
//   months: [
//     'January',
//     'February',
//     'March',
//     'April',
//     'May',
//     'June',
//     'July',
//     'August',
//     'September',
//     'October',
//     'November',
//     'December',
//   ],
//   shortMonths: [
//     'Jan',
//     'Feb',
//     'Mar',
//     'Apr',
//     'May',
//     'Jun',
//     'Jul',
//     'Aug',
//     'Sep',
//     'Oct',
//     'Nov',
//     'Dec',
//   ],
//   days: [
//     'Sunday',
//     'Monday',
//     'Tuesday',
//     'Wednesday',
//     'Thursday',
//     'Friday',
//     'Saturday',
//   ],
//   shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
//   goToToday: 'Go to today',
//   weekNumberFormatString: 'Week number {0}',
//   prevMonthAriaLabel: 'Previous month',
//   nextMonthAriaLabel: 'Next month',
//   prevYearAriaLabel: 'Previous year',
//   nextYearAriaLabel: 'Next year',
//   prevYearRangeAriaLabel: 'Previous year range',
//   nextYearRangeAriaLabel: 'Next year range',
//   closeButtonAriaLabel: 'Close',
//   monthPickerHeaderAriaLabel: '{0}, select to change the year',
//   yearPickerHeaderAriaLabel: '{0}, select to change the month',
// };

const ManageImportDialog: React.FC<ManageImportDialogProps> = ({
  handleImportDialogClose,
  dataCenterOptions,
  datacenterId,
  date,
  customer,
}) => {
  // state
  const [state, setState] = React.useState({
    isOpen: true,
    isLoading: false,
    selectedDatacenterId: datacenterId,
    date: date,
    customers: [],
    showCallender: false,
    isForAll: true,
  } as {
    isOpen: boolean;
    isLoading: boolean;
    selectedDatacenterId: string;
    date: Date;
    showCallender: boolean;
    isForAll: boolean;
    customers: Array<{
      text: string;
      secondaryText: string;
      id: string;
    }>;
  });

  React.useEffect(() => {
    let customers = [...state.customers];
    customers.push({
      text: customer.name,
      secondaryText: customer.regSapNo,
      id: customer._id as string,
    });
    setState({
      ...state,
      customers,
    });
  }, []);

  // custom hooks
  const params: any = useParams();
  const { showToast } = React.useContext(ToastContext);
  const tokenData = parseJwt();

  // file upload config
  const fileUploadConfig = FILE_UPLOAD_CONFIG[FILE_UPLOAD_MODULES.MANAGE_IMPORT_DIALOG];


  const onFileChange = async (files: any) => {
    if (files) {
      let selectedFile = files[0];
      // Validate file type
      const isValidType = validateFileType(
        selectedFile,
        fileUploadConfig.acceptedFileTypes
      );

      if (!isValidType) {
        showToast(
          getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription),
          "error"
        );
        return;
      }
      let data = {
        datacenterId: state.selectedDatacenterId,
        customerIds:
          state.isForAll === false ? state.customers.map((d) => d.id) : [],
        isForAll: state.isForAll,
      };
      setState({
        ...state,
        isLoading: true,
      });
      let res = await uptimeFileUpload(selectedFile, data);
      if (res && res.success) {
        showToast("Imported Successfully", "success");
      } else {
        showToast(res.message || "An error occurred while importing", "error");
      }
      setState({
        ...state,
        isLoading: false,
      });
      let file: any = document.getElementById("file");
      if (file) {
        file["value"] = "";
      }
    }
    handleImportDialogClose();
  };

  const uploadFile = () => {
    let file = document.getElementById("file");
    if (file) file.click();
  };

  const downloadSheet = () => {
    let date = moment(state.date).format("YYYY-MM");
    const arrDays = new Array(moment(date, "YYYY-MM").daysInMonth())
      .fill(null)
      .map((x, i) =>
        moment(date).startOf("month").add(i, "days").format("DD-MM-YYYY")
      );
    const data: any = [];
    arrDays.forEach((d: any, index: number) => {
      let obj = {
        Date: d || index,
        Power: "",
        "CRAC/PAC": "",
        "Access Control System": "",
        "CCTV System": "",
        Remark: "",
      };
      data.push(obj);
    });
    exportExcelSheet(data, "Uptime sample excel sheet");
    handleImportDialogClose();
  };

  const closeCallender = (ev?: any) => {
    setState({
      ...state,
      showCallender: false,
    });
  };

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    // suggestionsHeaderText: "Suggested Card holders",
    noResultsFoundText: "No Customer found",
  };

  const onFilterChanged = async (
    filterText: string,
    tagList?: IPersonaProps[]
  ): Promise<IPersonaProps[]> => {
    if (filterText && filterText.length >= 3) {
      let result = await getAllCustomers({
        search: filterText,
        offset: 0,
        limit: 12,
      });
      if (result?.success) {
        const cardHolderTag: IPersonaProps[] = [];
        // let tempcardHoldersList = result.data;

        setState({ ...state });

        result?.data.forEach((item: any) => {
          cardHolderTag.push({
            // key: item._id as string,
            // name: item.firstName + " " + item.lastName,
            // data:  item as IPersonaProps
            text: item.name,
            secondaryText: item.regSapNo,
            id: item._id,
          });
        });
        return cardHolderTag;
      } else {
        return [];
      }
    }
    return [];
  };

  return (
    <React.Fragment>
      <Dialog
        hidden={!state.isOpen}
        onDismiss={() => handleImportDialogClose()}
        modalProps={{
          isBlocking: true,
          styles: { main: { maxWidth: 450 } },
        }}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Import or Download sample",
          //   subText: "Import or download by selecting a month & datacenter",
        }}
      >
        <Stack horizontal tokens={childrenGap} horizontalAlign="space-between">
          <Stack.Item>
            <div ref={(calendarBtn) => (calendarButtonElement = calendarBtn!)}>
              <DefaultButton
                text={moment(state.date).format("MMM-YYYY")}
                onClick={() => setState({ ...state, showCallender: true })}
                className="month-picker-btn"
              />
            </div>
            {state.showCallender && (
              <Callout
                isBeakVisible={false}
                className="ms-DatePicker-callout"
                gapSpace={0}
                doNotLayer={false}
                target={calendarButtonElement}
                directionalHint={DirectionalHint.bottomLeftEdge}
                onDismiss={() => closeCallender()}
                setInitialFocus
              >
                <FocusTrapZone
                  firstFocusableSelector="ms-DatePicker-day--today"
                  isClickableOutsideFocusTrap
                >
                  <Calendar
                    // eslint-disable-next-line react/jsx-no-bind
                    onSelectDate={(date: Date | null | undefined) =>
                      setState({
                        ...state,
                        date: date as any,
                        showCallender: false,
                      })
                    }
                    onDismiss={() => closeCallender()}
                    isMonthPickerVisible={true}
                    value={state.date}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    strings={DayPickerStrings}
                    isDayPickerVisible={false}
                    highlightCurrentMonth={false}
                    highlightSelectedMonth={true}
                    showMonthPickerAsOverlay={false}
                    showGoToToday={false}
                  />
                </FocusTrapZone>
              </Callout>
            )}
          </Stack.Item>
          <Stack.Item>
            <Dropdown
              placeholder="Select a Datacenter"
              selectedKey={state.selectedDatacenterId}
              styles={{ dropdown: { width: 170 } }}
              options={dataCenterOptions}
              onChange={(e, v: IDropdownOption | undefined) => {
                if (v) {
                  setState({
                    ...state,
                    selectedDatacenterId: v.key as string,
                  });
                }
              }}
            />
          </Stack.Item>
        </Stack>
        <br />
        <Stack tokens={childrenGap}>
          {state.isForAll === false && (
            <Stack.Item>
              <NormalPeoplePicker
                inputProps={{
                  placeholder: "Select Customers",
                }}
                onResolveSuggestions={onFilterChanged}
                selectedItems={state.customers}
                removeButtonAriaLabel="Remove"
                pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={(items: any) => {
                  setState({ ...state, customers: items });
                }}
              />
            </Stack.Item>
          )}
          <Stack.Item>
            <Checkbox
              label="Common for All Customer in Floor"
              checked={state.isForAll}
              onChange={(e, v) => {
                setState({ ...state, isForAll: v === true });
              }}
            />
          </Stack.Item>
        </Stack>
        <br />
         <MessageBar
            messageBarType={MessageBarType.info}
            isMultiline={true}
          >
           {fileUploadConfig.hintText}
          </MessageBar>
        <DialogFooter className="d-flex">
          <input
            type="file"
            style={{ display: "none" }}
            accept={fileUploadConfig.acceptedFileTypes}
            onChange={(event: any) =>
              onFileChange(event.target.files)
            }
            id="file"
          />
          {![2, 4, 5].includes(tokenData.role) && (
            <PrimaryButton text="Import" onClick={uploadFile} />
          )}
          <DefaultButton
            text="Download Sample"
            onClick={() => downloadSheet()}
          />
        </DialogFooter>
      </Dialog>
    </React.Fragment>
  );
};

export default ManageImportDialog;
